<script lang="ts">
	// UC-17. The body of the "Edit my items" sheet: the whole menu to tick
	// from, and the cashier's set in its order to rearrange.
	//
	// A row is dragged by its grip, the one drag the till allows: an edit, not
	// a sale. Up and Down stay beside it, because a cheap touch screen or an
	// unsteady hand can lose a drag, and every move must be possible by tap
	// alone. A key on the grip moves the row too. The row being dragged is
	// marked data-dragging, which interaction.css lifts; the list reflows
	// under the finger, and scrolls itself near its edges.
	//
	// Ticking adds an item to the end of the set and unticking takes it out.
	// Every category's Choice is handed the whole set, so a tap answers with
	// the whole set changed by one item, in order, and nothing is merged here.

	import Button from '../components/Button.svelte';
	import Choice from '../components/Choice.svelte';
	import { message, type Language } from '../i18n/messages';
	import { arrowStep, dropAmong, edgeScroll, GripDrag, moveTo } from '../till/reorder';
	import type { components } from '../api/types';

	type Menu = components['schemas']['MenuBody'];

	interface Props {
		language: Language;
		menu: Menu;
		picks: string[];
		/** A refusal or a failure, in cashier words; empty when there is none. */
		error: string;
		onChange: (picks: string[]) => void;
	}

	let { language, menu, picks, error, onChange }: Props = $props();

	const categories = $derived(menu.categories ?? []);
	const items = $derived(menu.items ?? []);
	const names = $derived(new Map(items.map((item) => [item.id, item.name])));
	// A pick the menu no longer has is left out of the list; the server drops
	// it too, and saying so is the save's job.
	const ordered = $derived(picks.filter((id) => names.has(id)));

	// Moves within the list as shown, so a pick the menu dropped cannot hold
	// a place nobody can see.
	function move(id: string, by: -1 | 1) {
		const from = ordered.indexOf(id);
		const other = ordered[from + by];
		if (from < 0 || other === undefined) return;
		const next = [...ordered];
		next[from + by] = id;
		next[from] = other;
		onChange(next);
	}

	let list = $state<HTMLElement>();
	let dragging = $state<string | null>(null);

	function follow(event: PointerEvent) {
		const id = dragging;
		if (!id || !list) return;
		const others = [...list.querySelectorAll<HTMLElement>('[data-row-id]')].filter(
			(row) => row.dataset.rowId !== id,
		);
		const from = ordered.indexOf(id);
		const to = dropAmong(event.clientY, others);
		if (from >= 0 && to !== from) onChange(moveTo(ordered, from, to));
		const edge = list.getBoundingClientRect();
		const by = edgeScroll(event.clientY, edge.top, edge.bottom);
		if (by !== 0) list.scrollBy(0, by);
	}

	function drop() {
		dragging = null;
	}

	// Followed on the window (GripDrag says why).
	const drag = new GripDrag(follow, drop);

	function lift(id: string, event: PointerEvent) {
		if (drag.start(event)) dragging = id;
	}

	function key(id: string, event: KeyboardEvent) {
		const step = arrowStep(event);
		if (step !== 0) move(id, step);
	}

	$effect(() => () => drag.stop());
</script>

<div class="mine" data-testid="mine-editor">
	{#if error}
		<p class="alert" role="alert" data-testid="mine-error">{error}</p>
	{/if}
	<div class="columns">
		<section class="menu" data-scroll="">
			<p class="label">{message(language, 'mineWholeMenu')}</p>
			{#each categories as category (category.id)}
				{@const options = items
					.filter((item) => item.categoryId === category.id)
					.map((item) => ({ value: item.id, label: item.name, testId: `mine-pick-${item.id}` }))}
				{#if options.length > 0}
					<Choice
						testId="mine-category-{category.id}"
						title={category.name}
						mode="multi"
						min={0}
						stack
						{options}
						value={picks}
						onChange={(next) => onChange(next as string[])}
					/>
				{/if}
			{/each}
		</section>
		<section class="order" data-testid="mine-order" data-scroll="" bind:this={list}>
			<p class="label">
				{message(language, 'mineOrder')}
				<bdi class="n" dir="ltr">{ordered.length}</bdi>
			</p>
			{#if ordered.length === 0}
				<p class="none">{message(language, 'mineNone')}</p>
			{/if}
			{#each ordered as id, index (id)}
				<div
					class="row"
					data-testid="mine-row-{id}"
					data-row-id={id}
					data-dragging={dragging === id ? 'true' : 'false'}
				>
					<bdi class="n pos" dir="ltr">{index + 1}</bdi>
					<span class="name">{names.get(id)}</span>
					<Button
						testId="mine-up-{id}"
						label=""
						ariaLabel="{message(language, 'mineUp')}: {names.get(id)}"
						icon="up"
						square
						tone="text"
						size="sm"
						disabled={index === 0}
						onPress={() => move(id, -1)}
					/>
					<Button
						testId="mine-down-{id}"
						label=""
						ariaLabel="{message(language, 'mineDown')}: {names.get(id)}"
						icon="down"
						square
						tone="text"
						size="sm"
						disabled={index === ordered.length - 1}
						onPress={() => move(id, 1)}
					/>
					<Button
						testId="mine-grip-{id}"
						label=""
						ariaLabel="{message(language, 'mineDrag')}: {names.get(id)}"
						icon="grip"
						tone="text-quiet"
						size="sm"
						grip={{
							onPointerDown: (event) => lift(id, event),
							onKeyDown: (event) => key(id, event),
						}}
					/>
				</div>
			{/each}
		</section>
	</div>
</div>

<style>
	.mine {
		flex: 1;
		min-block-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.alert {
		margin: 0;
		font-size: var(--text-2xs);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}

	.columns {
		flex: 1;
		min-block-size: 0;
		display: flex;
		gap: var(--space-9);
	}

	.menu {
		flex: 1;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	/* As wide as the menu, so a long name fits on one or two lines. */
	.order {
		flex: 1;
		min-inline-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}

	.label {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.none {
		margin: 0;
		font-size: var(--text-xs);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.row {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding-inline: var(--space-5) var(--space-3);
		background: var(--surface-page);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-input);
	}

	.n {
		font-family: var(--font-mono);
	}

	.pos {
		flex: none;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.name {
		flex: 1;
		min-inline-size: 0;
		padding-block: var(--space-2);
		display: -webkit-box;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		overflow: hidden;
		font-size: var(--text-sm);
		font-weight: 600;
		line-height: var(--leading-snug);
		text-wrap: pretty;
	}
</style>
